import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  completeAutomationSurveyPrompt,
  dismissAutomationSurveyPrompt,
  startAutomationSurveyPrompt,
} from "../api/client";

function isQuestionAnswered(question, answers) {
  if (question.type === "info") return true;
  const answer = answers[question.id] || {};
  if (!question.required) return true;
  if (question.type === "multiple_choice") {
    const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
    return values.length > 0;
  }
  return Boolean(
    answer.optionId ||
      (answer.optionValue != null && answer.optionValue !== "" && !Array.isArray(answer.optionValue)) ||
      (answer.answerText && String(answer.answerText).trim()) ||
      answer.answerNumber != null
  );
}

function buildAnswersPayload(questions, answers) {
  return questions.flatMap((question) => {
    const answer = answers[question.id] || {};
    if (question.type === "info") return [];

    if (question.type === "multiple_choice") {
      const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
      return values.map((value) => {
        const option =
          (question.options || []).find((item) => String(item.value) === String(value)) || null;
        return {
          questionId: question.id,
          optionId: option?.id || null,
          optionValue: value,
          answerText: option?.label || String(value),
          answerScore: option?.score ?? null,
        };
      });
    }

    return [
      {
        questionId: question.id,
        optionId: answer.optionId,
        optionValue: answer.optionValue,
        answerText: answer.answerText,
        answerNumber: answer.answerNumber,
        answerScore: answer.answerScore,
      },
    ];
  });
}

function QuestionInput({ question, answer, onChange }) {
  if (question.type === "info") {
    return (
      <Typography variant="body2" color="text.secondary">
        {question.description || "Continue para a próxima etapa."}
      </Typography>
    );
  }

  if (question.type === "nps") {
    return (
      <ToggleButtonGroup
        exclusive
        value={answer.answerNumber ?? null}
        onChange={(_, value) => onChange({ answerNumber: value, answerScore: value })}
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        {Array.from(
          { length: Number(question.scaleMax || 10) - Number(question.scaleMin || 0) + 1 },
          (_, index) => Number(question.scaleMin || 0) + index
        ).map((score) => (
          <ToggleButton key={score} value={score}>
            {score}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }

  if (question.type === "rating_scale" || question.type === "emoji_scale") {
    return (
      <ToggleButtonGroup
        exclusive
        value={answer.answerNumber ?? null}
        onChange={(_, value) => onChange({ answerNumber: value, answerScore: value })}
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        {(question.options || []).map((option) => (
          <ToggleButton key={option.id || option.value} value={option.score ?? Number(option.value)}>
            {question.type === "emoji_scale"
              ? ["😡", "🙁", "😐", "🙂", "😍"][Number(option.position || 1) - 1] || option.label
              : option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }

  if (question.type === "multiple_choice") {
    const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
    return (
      <Stack spacing={1}>
        {(question.options || []).map((option) => (
          <FormControlLabel
            key={option.id || option.value}
            control={
              <Checkbox
                checked={values.includes(option.value)}
                onChange={(event) => {
                  const nextValues = event.target.checked
                    ? [...values, option.value]
                    : values.filter((item) => item !== option.value);
                  onChange({ optionValue: nextValues, answerText: nextValues.join(", ") });
                }}
              />
            }
            label={option.label}
          />
        ))}
      </Stack>
    );
  }

  if (question.type === "single_choice" || question.type === "yes_no") {
    return (
      <RadioGroup
        value={answer.optionId || answer.optionValue || ""}
        onChange={(event) => {
          const option =
            (question.options || []).find(
              (item) =>
                String(item.id) === String(event.target.value) ||
                String(item.value) === String(event.target.value)
            ) || null;
          onChange({
            optionId: option?.id || null,
            optionValue: option?.value || event.target.value,
            answerScore: option?.score ?? null,
          });
        }}
      >
        {(question.options || []).map((option) => (
          <FormControlLabel
            key={option.id || option.value}
            value={option.id || option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <TextField
      fullWidth
      multiline={question.type === "long_text"}
      minRows={question.type === "long_text" ? 4 : 1}
      placeholder="Digite sua resposta"
      value={answer.answerText || ""}
      onChange={(event) => onChange({ answerText: event.target.value })}
    />
  );
}

export default function AutomationSurveyModal({ prompt, open, onClose, onCompleted }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const onCloseRef = useRef(onClose);
  const onCompletedRef = useRef(onCompleted);
  const loadedPromptIdRef = useRef(null);

  onCloseRef.current = onClose;
  onCompletedRef.current = onCompleted;

  const finishAndClose = (promptId) => {
    loadedPromptIdRef.current = null;
    if (promptId) onCompletedRef.current?.({ promptId });
    onCloseRef.current?.({ completed: true });
  };

  const mandatory = Boolean(prompt?.mandatory);
  const questions = survey?.questions || [];
  const currentQuestion = questions[currentQuestionIndex] || null;
  const accentColor = survey?.branding?.primaryColor || survey?.branding?.color || "#1976d2";

  const progress = questions.length
    ? Math.round(((currentQuestionIndex + 1) / questions.length) * 100)
    : 0;

  const nextPendingIndex = useMemo(
    () => questions.findIndex((question) => !isQuestionAnswered(question, answers)),
    [questions, answers]
  );

  useEffect(() => {
    if (!open || !prompt?.id) {
      if (!open) loadedPromptIdRef.current = null;
      return undefined;
    }

    const promptId = Number(prompt.id);
    if (loadedPromptIdRef.current === promptId) return undefined;

    loadedPromptIdRef.current = promptId;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setSurvey(null);
      setAnswers({});
      setCurrentQuestionIndex(0);
      try {
        const payload = await startAutomationSurveyPrompt(promptId);
        if (cancelled) return;
        if (payload?.alreadyCompleted) {
          finishAndClose(promptId);
          return;
        }
        if (payload?.alreadyDismissed) {
          loadedPromptIdRef.current = null;
          onCloseRef.current?.({ dismissed: true });
          return;
        }
        setSurvey(payload?.survey || null);
      } catch (e) {
        if (!cancelled) {
          loadedPromptIdRef.current = null;
          const message = e?.response?.data?.message || e?.message || "";
          if (/respondida|dispensada/i.test(String(message))) {
            finishAndClose(promptId);
            return;
          }
          setError(message || "Não foi possível abrir a pesquisa.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, prompt?.id]);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], ...value },
    }));
  };

  const handleDismiss = async () => {
    if (mandatory) return;
    try {
      await dismissAutomationSurveyPrompt(prompt.id);
      onClose?.({ dismissed: true });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível fechar a pesquisa.");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = buildAnswersPayload(questions, answers);
      await completeAutomationSurveyPrompt(prompt.id, payload);
      finishAndClose(prompt.id);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Erro ao registrar respostas.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDialogClose = (_, reason) => {
    if (mandatory && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    if (!mandatory) handleDismiss();
  };

  const title = prompt?.surveyTitle || survey?.title || "Pesquisa";

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={mandatory}
    >
      <DialogTitle sx={{ pr: mandatory ? 3 : 6 }}>
        {title}
        {!mandatory ? (
          <IconButton
            aria-label="Fechar"
            onClick={handleDismiss}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {mandatory ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta pesquisa é obrigatória. Responda para continuar.
          </Alert>
        ) : null}

        {prompt?.solicitacaoId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Vinculada à solicitação #{prompt.solicitacaoId}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && survey && questions.length === 0 ? (
          <Alert severity="info">Esta pesquisa não possui perguntas.</Alert>
        ) : null}

        {!loading && currentQuestion ? (
          <Stack spacing={2}>
            <Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "action.hover",
                  "& .MuiLinearProgress-bar": { bgcolor: accentColor },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Pergunta {Math.min(currentQuestionIndex + 1, questions.length)} de {questions.length}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1">
                {currentQuestionIndex + 1}. {currentQuestion.title}
                {currentQuestion.required ? " *" : ""}
              </Typography>
              {currentQuestion.description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {currentQuestion.description}
                </Typography>
              ) : null}
            </Box>

            <QuestionInput
              question={currentQuestion}
              answer={answers[currentQuestion.id] || {}}
              onChange={(value) => updateAnswer(currentQuestion.id, value)}
            />

            {!isQuestionAnswered(currentQuestion, answers) ? (
              <Alert severity="info" sx={{ py: 0 }}>
                Responda esta pergunta para continuar.
              </Alert>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      {!loading && survey && questions.length > 0 ? (
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
          >
            Voltar
          </Button>

          <Stack direction="row" spacing={1}>
            {!mandatory ? (
              <Button color="inherit" onClick={handleDismiss}>
                Fechar
              </Button>
            ) : null}

            {currentQuestionIndex < questions.length - 1 ? (
              <Button
                variant="contained"
                disabled={!isQuestionAnswered(currentQuestion, answers)}
                onClick={() =>
                  setCurrentQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))
                }
              >
                Continuar
              </Button>
            ) : (
              <>
                {nextPendingIndex >= 0 ? (
                  <Button variant="outlined" onClick={() => setCurrentQuestionIndex(nextPendingIndex)}>
                    Revisar pendências
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || !isQuestionAnswered(currentQuestion, answers)}
                >
                  {submitting ? "Enviando..." : "Concluir pesquisa"}
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
